import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { rpaBridge, type ComputerUseReadiness } from '@/tauri/rpa';
import { bridgeErrorKind } from '@/utils/logger/bridge-error-kind';
import { logger } from '@/utils/logger/logger';

const PERMISSION_POLL_INTERVAL_MS = 1500;

/** `live` re-reads every 1.5s and on window focus; `on_focus` only when the user comes back from System Settings */
export type ReadinessPolling = 'off' | 'on_focus' | 'live';

/**
 * Live computer-use readiness. Reads when polling starts, then as `polling`
 * says. `null` until the first read lands, and whenever `off`. A failed read
 * keeps the last good one. Never prompts.
 */
export function useComputerUseReadiness(
  polling: ReadinessPolling
): ComputerUseReadiness | null {
  const active = polling !== 'off';
  const { data, error, refetch } = useQuery({
    queryKey: ['computer-use-readiness'],
    queryFn: () => rpaBridge.readComputerUseReadiness(),
    enabled: active,
    refetchOnMount: 'always',
    refetchInterval: polling === 'live' ? PERMISSION_POLL_INTERVAL_MS : false,
    // The user is in System Settings while Heidi waits for the grant
    refetchIntervalInBackground: true,
    // TanStack watches page visibility, which does not change when the user switches apps
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
    // Forget the last read once nothing reads it, so `off` and a remount start fresh
    gcTime: 0,
    retry: false,
  });

  // A failing bridge fails on every poll, so warn once when reads start failing
  const failing = error !== null;
  useEffect(() => {
    if (!failing) return;
    logger.warn('[cua-readiness] Failed to read live readiness', {
      error_kind: bridgeErrorKind(error),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per failing streak, not per error object
  }, [failing]);

  useEffect(() => {
    if (!active) return;
    const onFocus = () => void refetch();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [active, refetch]);

  return active ? (data ?? null) : null;
}
